import React, { useState } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { Lock, Mail, AlertCircle, UserPlus, LogIn } from 'lucide-react';

interface LoginProps {
  onLoginSuccess: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const logLogin = async (userId: string) => {
      if (isSupabaseConfigured()) {
          let details: any = {};
          try {
            // Tentative de récupération de l'IP publique
            const res = await fetch('https://api.ipify.org?format=json');
            if (res.ok) {
              const data = await res.json();
              details = { ip: data.ip };
            }
          } catch (e) {
            console.warn('Failed to fetch IP for audit log', e);
          }

          await supabase.from('audit_logs').insert({
              user_id: userId,
              action: 'LOGIN',
              entity: 'AUTH',
              details: details
          });
      }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    if (!isSupabaseConfigured()) {
      // Mock login for demo purposes if no API key is set
      setTimeout(() => {
        onLoginSuccess();
        setLoading(false);
      }, 1000);
      return;
    }

    if (isSignUp) {
      // SIGN UP
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          // Redirige vers l'URL actuelle (localhost ou prod) après confirmation email
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) {
        setError(error.message);
      } else {
        setMessage("Inscription réussie ! Vérifiez vos emails pour confirmer votre compte.");
        setIsSignUp(false); // Switch back to login view to allow them to wait
      }
    } else {
      // LOGIN
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Gestion spécifique des erreurs courantes
        if (error.message.includes("Email not confirmed")) {
          setError("Veuillez confirmer votre adresse email avant de vous connecter.");
        } else if (error.message.includes("Invalid login credentials")) {
           setError("Email ou mot de passe incorrect.");
        } else {
          setError(error.message);
        }
      } else {
        if (data.user) {
            await logLogin(data.user.id);
        }
        onLoginSuccess();
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md border border-slate-200">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center text-white mx-auto mb-4">
            <Lock size={24} />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">SIL Mycobactérie IPM</h1>
          <p className="text-slate-500 text-sm mt-2">{isSignUp ? 'Créer un compte' : 'Connexion au système'}</p>
        </div>

        {!isSupabaseConfigured() && (
          <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg text-sm mb-6 flex items-start gap-3 border border-yellow-200">
            <AlertCircle className="shrink-0 mt-0.5" size={16} />
            <div>
              <p className="font-semibold">Mode Démo</p>
              <p>Supabase n'est pas configuré. Vous pouvez vous connecter avec n'importe quoi.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-6 border border-red-200">
            {error}
          </div>
        )}

        {message && (
          <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm mb-6 border border-green-200">
            {message}
          </div>
        )}

        <form onSubmit={handleAuth} className="space-y-5">
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom complet</label>
              <input
                type="text"
                required={isSignUp}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                placeholder="Ex: Jean Dupont"
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                placeholder="biologiste@labo.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                placeholder="••••••••"
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
          >
            {loading ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
            ) : (
              <>
                {isSignUp ? <UserPlus size={18}/> : <LogIn size={18}/>}
                {isSignUp ? "S'inscrire" : 'Se connecter'}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button 
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError(null);
              setMessage(null);
            }}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
          >
            {isSignUp ? "J'ai déjà un compte" : "Pas encore de compte ? Créer un compte"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;